import { baseApi } from './baseApi'
import { activateLocale, currentLocale, type Locale } from './i18n'
import { sessionCleared, sessionEstablished } from './sessionSlice'

import { roleLabelOf } from '@/shared/constants/roles'
import { registerMockRoutes } from '@/shared/lib/mockBaseQuery'
import type {
  ApiEnvelope,
  MeApi,
  RoleSwitchApi,
  SessionApi,
} from '@/shared/types/apiContract.types'
import type { SessionUser } from '@/shared/types/session.types'

function toShortName(fullName: string): string {
  const [first, ...rest] = fullName.trim().split(/\s+/)
  if (!first || rest.length === 0) return fullName
  return `${first[0]}. ${rest.join(' ')}`
}

function adaptSessionUser(session: SessionApi): SessionUser {
  const role = roleLabelOf(session.user.roleCode)
  return {
    id: session.user.id,
    name: session.user.fullName,
    shortName: toShortName(session.user.fullName),
    email: session.user.email,
    roleId: session.user.roleCode,
    /** El canje de sesión no trae foto: llega con el `GET /me` que sigue. */
    photoUrl: null,
    roleCode: role.short,
    roleTitle: role.title,
    hotel: null,
    department: null,
    zones: [],
    locale: currentLocale(),
    permissions: [],
    /** El canje no trae las funciones: llegan con el `GET /me` que sigue. */
    availableRoles: [{ code: session.user.roleCode, name: role.title }],
  }
}

/**
 * Funciones del ambiente simulado: con una sola (la de siempre) el menú no
 * ofrece cambiar. Las pruebas que necesitan varias simulan la sesión.
 */
const mockRoles = { active: 'ROL-V-01' }

const MOCK_SESSION_FLAG = 'oranje-mock-session'

const MOCK_SESSION: SessionApi = {
  accessToken: 'mock-access-token',
  expiresIn: 900,
  user: {
    id: 'usr-ana-ruiz',
    email: 'ana.ruiz@oranje.mx',
    fullName: 'Ana Ruiz',
    roleCode: 'ROL-V-01',
  },
}

registerMockRoutes([
  {
    method: 'POST',
    path: '/auth/session',
    resolve: (): ApiEnvelope<SessionApi> => {
      localStorage.setItem(MOCK_SESSION_FLAG, 'true')
      return { data: MOCK_SESSION }
    },
  },
  {
    method: 'POST',
    path: '/auth/refresh',
    resolve: (): ApiEnvelope<SessionApi> => {
      if (localStorage.getItem(MOCK_SESSION_FLAG) !== 'true') {
        throw new Error('REFRESH_MISSING')
      }
      return { data: MOCK_SESSION }
    },
  },
  {
    method: 'POST',
    path: '/auth/active-role',
    resolve: ({ body }): ApiEnvelope<RoleSwitchApi> => {
      const { roleCode } = (body ?? {}) as { roleCode?: string }
      if (roleCode !== MOCK_SESSION.user.roleCode) throw new Error('Esa función no es tuya')
      mockRoles.active = roleCode
      return { data: { ...MOCK_SESSION, user: { ...MOCK_SESSION.user, roleCode } } }
    },
  },
  {
    method: 'POST',
    path: '/auth/logout',
    resolve: (): null => {
      localStorage.removeItem(MOCK_SESSION_FLAG)
      return null
    },
  },
  {
    method: 'GET',
    path: '/me',
    resolve: (): ApiEnvelope<MeApi> => ({
      data: {
        id: MOCK_SESSION.user.id,
        email: MOCK_SESSION.user.email,
        fullName: MOCK_SESSION.user.fullName,
        photoUrl: null,
        role: {
          code: MOCK_SESSION.user.roleCode,
          name: 'Business Developer',
          department: 'Ventas',
        },
        primaryRole: { code: MOCK_SESSION.user.roleCode, name: 'Business Developer' },
        availableRoles: [{ code: MOCK_SESSION.user.roleCode, name: 'Business Developer' }],
        hotel: null,
        department: null,
        locale: 'es',
        zones: [],
        /**
         * La UNIÓN de todo lo que la UI consulta con `useCan`: en modo mock
         * ningún botón se esconde. Los recortes por rol solo aplican contra la
         * API real, que manda la lista exacta de `identity.role_permission`.
         */
        permissions: [
          'catalogs.manage',
          'payroll.validate',
          'pipeline.read',
          'pipeline.create_prospect',
          'pipeline.create_contact_attempt',
          'pipeline.close_cycle',
          'pipeline.delete_hotel',
          'pipeline.import_prospects',
          'pipeline.update_hotel_profile',
          'proposals.read',
          'proposals.create',
          'proposals.send',
          'blacklist.read',
          'blacklist.create',
          'blacklist.lift',
          'conversion.approve',
          'conversion.create_hotel_user',
          'hotel.punch_qr',
          'terms_and_conditions.create',
          'terms_and_conditions.update',
          'terms_and_conditions.approve',
          'requisitions.create',
          'requisitions.take',
          'requisitions.delete_empty',
          'requisitions.delete_with_positions',
          'requisitions.request_pay_adjustment',
          'recruitment.create_worker',
          'recruitment.update_worker',
          'recruitment.validate_signup',
          'recruitment.change_worker_state_any',
          'recruitment.search_candidates',
          'messaging.manage',
          'payroll.read',
          'payroll.generate',
          'payroll.authorize',
          'payroll.mark_paid',
          'payroll.manage_deductions',
          'timesheet.read_all_hotels',
          'timesheet.approve_hours',
          'timesheet.review_punches',
          'timesheet.create_manual_punch',
          'schedule.update',
          'audits.create',
          'audits.read',
          'audits.update',
          'users.manage_corporate_email',
          'settings.manage',
          'users.invite_hotel',
          'users.approve_hotel',
        ],
      },
    }),
  },
])

let lastAccessToken = ''

function captureToken(raw: ApiEnvelope<SessionApi>): SessionUser {
  lastAccessToken = raw.data.accessToken
  return adaptSessionUser(raw.data)
}

export const sessionApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getSession: build.query<SessionUser, void>({
      query: () => '/me',
      transformResponse: (raw: ApiEnvelope<MeApi>): SessionUser => ({
        id: raw.data.id,
        name: raw.data.fullName,
        shortName: toShortName(raw.data.fullName),
        email: raw.data.email,
        roleId: raw.data.role.code,
        photoUrl: raw.data.photoUrl,
        roleCode: roleLabelOf(raw.data.role.code).short,
        roleTitle: raw.data.role.name,
        hotel: raw.data.hotel,
        department: raw.data.department
          ? { id: raw.data.department.id, name: raw.data.department.name }
          : null,
        zones: raw.data.zones,
        locale: raw.data.locale === 'en' ? 'en' : 'es',
        permissions: raw.data.permissions,
        availableRoles: raw.data.availableRoles ?? [
          { code: raw.data.role.code, name: raw.data.role.name },
        ],
      }),
      /** Al entrar gana el idioma guardado en la persona (D-36). */
      onQueryStarted: async (_arg, { queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled
          if (data.locale !== currentLocale()) activateLocale(data.locale)
        } catch {
          /* Sin sesión no hay preferencia que aplicar. */
        }
      },
    }),

    /** Guarda el idioma en la persona, para que la siga entre dispositivos. */
    updateMyLocale: build.mutation<void, Locale>({
      query: (locale) => ({ url: '/me', method: 'PATCH', body: { locale } }),
    }),

    /**
     * Cambiar de función (funciones adicionales, 2026-11-02). El token nuevo
     * reemplaza al anterior y TODO lo cacheado se tira: permisos, menús y
     * datos eran de la función anterior. El `GET /me` se vuelve a pedir solo.
     */
    switchActiveRole: build.mutation<SessionUser, string>({
      query: (roleCode) => ({
        url: '/auth/active-role',
        method: 'POST',
        body: { roleCode },
        credentials: 'include',
      }),
      transformResponse: (raw: ApiEnvelope<RoleSwitchApi>): SessionUser =>
        captureToken({ data: { ...raw.data } }),
      onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled
          dispatch(baseApi.util.resetApiState())
          dispatch(sessionEstablished({ user: data, accessToken: lastAccessToken }))
        } catch {
          /* La función sigue siendo la de antes; la pantalla avisa el motivo. */
        }
      },
    }),

    createSession: build.mutation<SessionUser, { idToken: string }>({
      query: (body) => ({
        url: '/auth/session',
        method: 'POST',
        body,
        credentials: 'include',
      }),
      transformResponse: captureToken,
      onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled
          dispatch(baseApi.util.resetApiState())
          dispatch(sessionEstablished({ user: data, accessToken: lastAccessToken }))
        } catch {
          dispatch(sessionCleared())
        }
      },
    }),

    refreshSession: build.mutation<SessionUser, void>({
      query: () => ({ url: '/auth/refresh', method: 'POST', credentials: 'include' }),
      transformResponse: captureToken,
      onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled
          dispatch(sessionEstablished({ user: data, accessToken: lastAccessToken }))
        } catch {
          dispatch(sessionCleared())
        }
      },
    }),

    logout: build.mutation<void, void>({
      query: () => ({ url: '/auth/logout', method: 'POST', credentials: 'include' }),
      onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
        try {
          await queryFulfilled
        } catch {
          /* La sesión local se limpia igual aunque falle la llamada: sin
             `catch`, `queryFulfilled` rechazado se relanzaba solo tras el
             `finally` y quedaba como una promesa sin atrapar. */
        } finally {
          dispatch(sessionCleared())
          dispatch(baseApi.util.resetApiState())
        }
      },
    }),
  }),
})

export const {
  useGetSessionQuery,
  useCreateSessionMutation,
  useRefreshSessionMutation,
  useLogoutMutation,
  useSwitchActiveRoleMutation,
  useUpdateMyLocaleMutation,
} = sessionApi
